import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Landmark, ShieldCheck, ShieldAlert, ArrowUpRight, ArrowDownLeft, Scale, RefreshCw } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', purple: '#7C5CBF' };

const money = v => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = ts => (ts ? new Date(ts).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const TYPE_META = {
  premium_receipt: { label: 'Premium receipt', color: C.green },
  insurer_remittance: { label: 'Insurer remittance', color: C.blue },
  statutory_remittance: { label: 'GST & stamp duty', color: C.amber },
  commission_transfer: { label: 'Commission sweep', color: C.purple },
};

const Kpi = ({ label, value, sub, color, testid }) => (
  <Card className="p-4 rounded-md bg-white" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid={testid}>
    <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</span>
    <div className="text-xl font-bold font-mono mt-1" style={{ color }}>{value}</div>
    {sub && <div className="text-[11px] mt-0.5" style={{ color: C.slate }}>{sub}</div>}
  </Card>
);

export default function TrustAccountPage() {
  const api = useApi();
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [recons, setRecons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    return Promise.all([
      api.get('/finance/trust/summary').then(r => setSummary(r.data)),
      api.get('/finance/trust/ledger').then(r => setLedger(r.data.entries || [])),
      api.get('/finance/trust/reconciliations').then(r => setRecons(r.data.reconciliations || [])),
    ]).catch(e => console.error('trust:', e?.message)).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const remit = async (kind, label) => {
    setBusy(kind);
    try {
      const { data } = await api.post(`/finance/trust/remit/${kind}`);
      toast.success(`${label}: ${data.message}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    setBusy('');
  };

  const reconcile = async () => {
    setBusy('recon');
    try {
      const { data } = await api.post('/finance/trust/reconcile');
      toast.success(`Reconciliation ${data.status.toUpperCase()} — trust ${money(data.trust_balance)} vs liabilities ${money(data.client_liabilities)}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reconciliation failed'); }
    setBusy('');
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}</div>;
  if (!summary) return <p className="text-sm p-6" style={{ color: C.slate }}>Trust account unavailable.</p>;

  const ok = summary.ring_fenced;

  return (
    <div className="space-y-5" data-testid="trust-account-page">
      {/* Ring-fence status */}
      <Card className="p-5 rounded-md flex flex-col md:flex-row md:items-center gap-4" data-testid="ring-fence-banner"
        style={{ background: ok ? 'rgba(29,158,117,0.06)' : 'rgba(226,75,74,0.06)', border: `1px solid ${ok ? 'rgba(29,158,117,0.35)' : 'rgba(226,75,74,0.4)'}` }}>
        <div className="w-11 h-11 rounded-sm flex items-center justify-center shrink-0" style={{ background: (ok ? C.green : C.red) + '18' }}>
          {ok ? <ShieldCheck className="w-6 h-6" style={{ color: C.green }} /> : <ShieldAlert className="w-6 h-6" style={{ color: C.red }} />}
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold" style={{ color: ok ? C.green : C.red }} data-testid="ring-fence-status">
            {ok ? 'RING-FENCED — client money fully isolated' : 'BREACH — trust balance below client liabilities'}
          </p>
          <p className="text-xs mt-0.5" style={{ color: C.slate }}>
            Trust balance {money(summary.trust_balance)} vs client liabilities {money(summary.client_liabilities)} (net premium owed to insurers + statutory charges).
            Commission is only released once earned, and never below the fence.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={reconcile} disabled={!!busy} data-testid="run-reconciliation-btn" className="rounded-sm shrink-0">
          {busy === 'recon' ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Scale className="w-4 h-4 mr-1.5" />} Run Reconciliation
        </Button>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Trust balance" value={money(summary.trust_balance)} color={C.navy} sub="Segregated trust bank account" testid="kpi-trust-balance" />
        <Kpi label="Owed to insurers" value={money(summary.owed_insurer)} color={C.blue} sub={`${summary.counts?.insurer || 0} invoice(s) awaiting remittance`} testid="kpi-owed-insurer" />
        <Kpi label="Statutory payable" value={money(summary.statutory_payable)} color={C.amber} sub="GST + stamp duty held" testid="kpi-statutory" />
        <Kpi label="Commission transferable" value={money(summary.commission_transferable)} color={C.purple} sub="Earned — safe to sweep" testid="kpi-commission" />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => remit('insurer', 'Insurer remittance')} disabled={!!busy || summary.owed_insurer <= 0}
          data-testid="remit-insurer-btn" className="rounded-sm text-white" style={{ background: C.blue }}>
          <ArrowUpRight className="w-4 h-4 mr-1.5" /> Remit Net Premium to Insurer
        </Button>
        <Button size="sm" onClick={() => remit('statutory', 'Statutory remittance')} disabled={!!busy || summary.statutory_payable <= 0}
          data-testid="remit-statutory-btn" className="rounded-sm text-white" style={{ background: C.amber }}>
          <ArrowUpRight className="w-4 h-4 mr-1.5" /> Remit GST & Stamp Duty
        </Button>
        <Button size="sm" onClick={() => remit('commission', 'Commission sweep')} disabled={!!busy || summary.commission_transferable <= 0}
          data-testid="sweep-commission-btn" className="rounded-sm text-white" style={{ background: C.purple }}>
          <ArrowDownLeft className="w-4 h-4 mr-1.5" /> Sweep Earned Commission
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Ledger */}
        <Card className="p-5 rounded-md bg-white lg:col-span-2" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="trust-ledger">
          <div className="flex items-center gap-2 mb-3">
            <Landmark className="w-4 h-4" style={{ color: C.teal }} />
            <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Trust Ledger</h3>
            <span className="text-[11px]" style={{ color: C.slate }}>({ledger.length} most recent)</span>
          </div>
          {ledger.length === 0 ? (
            <p className="text-xs" style={{ color: C.slate }}>No movements yet — premiums land here the moment a bank transfer is receipted.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left" style={{ color: C.slate }}>
                    <th className="pb-2 font-medium">Date</th><th className="pb-2 font-medium">Type</th>
                    <th className="pb-2 font-medium">Detail</th><th className="pb-2 font-medium text-right">Amount</th>
                    <th className="pb-2 font-medium text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map(e => {
                    const m = TYPE_META[e.type] || { label: e.type, color: C.slate };
                    const credit = e.direction === 'credit';
                    return (
                      <tr key={e.entry_id} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`ledger-entry-${e.entry_id}`}>
                        <td className="py-1.5 whitespace-nowrap" style={{ color: C.slate }}>{when(e.ts)}</td>
                        <td className="py-1.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: m.color + '15', color: m.color }}>{m.label}</span></td>
                        <td className="py-1.5" style={{ color: C.navy }}>{e.description}{e.reference ? ` · ${e.reference}` : ''}</td>
                        <td className="py-1.5 text-right font-mono font-semibold" style={{ color: credit ? C.green : C.red }}>{credit ? '+' : '−'}{money(e.amount)}</td>
                        <td className="py-1.5 text-right font-mono" style={{ color: C.navy }}>{money(e.balance_after)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Reconciliations */}
        <Card className="p-5 rounded-md bg-white" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="trust-reconciliations">
          <h3 className="text-sm font-semibold mb-1" style={{ color: C.navy }}>Reconciliation History</h3>
          <p className="text-[11px] mb-3" style={{ color: C.slate }}>Runs automatically on the 1st of each month — audit-ready evidence the fence held.</p>
          {recons.length === 0 ? (
            <p className="text-xs" style={{ color: C.slate }}>No reconciliations yet — run your first one above.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {recons.map(r => (
                <div key={r.recon_id} className="p-2.5 rounded-sm" style={{ background: '#F4F6F9' }} data-testid={`recon-${r.recon_id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold" style={{ color: C.navy }}>{r.month} · {when(r.run_at)}</span>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                      style={{ background: (r.status === 'reconciled' ? C.green : C.red) + '18', color: r.status === 'reconciled' ? C.green : C.red }}>
                      {r.status.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-[11px] mt-1" style={{ color: C.slate }}>
                    Trust {money(r.trust_balance)} · liabilities {money(r.client_liabilities)} · commission held {money(r.commission_transferable)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
